import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";

import { isDev, isProd, isTest, isUat } from "@vega-core/utils/global";

const FOUR_HOURS = 1000 * 60 * 60 * 4;
const ONE_HOUR = 1000 * 60 * 60;

export function useInactivityAutoLogout({
  logoutPath,
  enableDebugLogs = false,
}: {
  logoutPath: string;
  enableDebugLogs?: boolean;
}) {
  const timeoutMs = useMemo(() => {
    let timeout = FOUR_HOURS;

    if (isUat || isProd) {
      timeout = ONE_HOUR;
    }

    if (isDev || isTest) {
      timeout = FOUR_HOURS;
    }

    if (enableDebugLogs) {
      console.log("Inactivity auto-logout timeout:", timeout);
    }

    return timeout;
  }, [enableDebugLogs]);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const onLogout = useCallback(() => {
    if (enableDebugLogs) {
      console.log("Inactivity timeout reached. Initiating auto-logout.");
    }
    navigate(logoutPath);
  }, [navigate, logoutPath, enableDebugLogs]);

  const handleTimeout = useCallback(() => {
    if (enableDebugLogs) {
      console.log("Inactivity timeout reached. Initiating auto-logout.");
    }
    onLogout();
  }, [onLogout, enableDebugLogs]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(handleTimeout, timeoutMs);

    if (enableDebugLogs) {
      console.log(`Auto-logout timer reset (${timeoutMs / 1000}s remaining)`);
    }
  }, [timeoutMs, handleTimeout, enableDebugLogs]);

  useEffect(() => {
    // User activity events that will reset the auto-logout timer
    const activityEvents = [
      "mousemove",
      "mousedown",
      "keypress",
      "touchstart",
      "scroll",
      "click",
    ] as const;

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners with capture phase for better coverage
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, {
        capture: true,
        passive: true,
      });
    });

    // Initialize the timer
    resetTimer();

    // Cleanup on unmount
    return () => {
      if (enableDebugLogs) {
        console.log("Cleaning up auto-logout listeners and timer");
      }

      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity, { capture: true });
      });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer, enableDebugLogs]);
}
